/**
 * The PUBLIC catalog's readers (APP-ENTITLEMENT-001 Phase 11): what `GET /v1/catalog/apps` answers — the offered
 * (and coming-soon) apps, their dimensions with PUBLISHED prices, the suggestions, the processing rate, the annual
 * rule and the trial length — read ONE way by every surface that prices from it: the marketing pricing section on
 * nine sites, the signup basket, the Billing page's tiles and the console's preview.
 *
 * Nothing here holds a price or a word. A reader answers rows, numbers and keys; the words belong to the bundle of
 * the surface that renders them, and the arithmetic to `quoteBasket`.
 *
 * These readers were born as three copies — pos-web's basket rules, account-apps-ui's price copy and pos-web's
 * catalog resource — which is the drift a public read invites; they live here now, once.
 *
 * @module @puffin-core/app-catalog/rules/publicCatalog
 */

'use strict';

const { getApp } = require('../catalog');
const { isAppCatalogRefusal, APP_CATALOG_REFUSAL } = require('../refusal');
const {
  APP_KIND,
  BILLING_CYCLE,
  DIMENSION_KEY,
  DIMENSION_KIND,
  PRICING_MODEL,
  QUANTITY_SOURCE,
} = require('../vocabulary');
const { quoteBasket } = require('./quoteBasket');

/**
 * @typedef {Object} PublicPrice
 * @property {string} billingCycle - `monthly` | `annual`.
 * @property {number} unitAmountCents
 * @property {string} currency
 */

/**
 * @typedef {Object} PublicDimension
 * @property {string} appKey
 * @property {string} dimensionKind - `size` | `consumable`.
 * @property {string} dimensionKey
 * @property {string} quantitySource - `derived` | `declared`.
 * @property {?string} parentDimensionKey
 * @property {number} includedQuantity
 * @property {?number} blockSize
 * @property {number} sortOrder
 * @property {PublicPrice[]} prices - the PUBLISHED prices, one per cycle; empty while none is published.
 */

/**
 * @typedef {Object} PublicEntry
 * @property {string} appKey
 * @property {string} kind
 * @property {string} label
 * @property {?string[]} attachesTo
 * @property {string[]} capabilityKeys
 * @property {string} pricingModel
 * @property {?number} shareBps
 * @property {number} trialDays
 * @property {boolean} isSellable
 * @property {boolean} comingSoon
 * @property {boolean} isOffered
 * @property {?string} launchAppKey
 * @property {number} sortOrder
 * @property {PublicDimension[]} dimensions
 */

/**
 * @typedef {Object} PublicCatalog
 * @property {PublicEntry[]} apps
 * @property {Object[]} suggestions
 * @property {{rateBps: number, fixedCents: number}} processing
 * @property {number} annualMonthsCharged
 * @property {number} trialDays
 */

const isNonNegativeInteger = (value) => Number.isInteger(value) && value >= 0;

/**
 * The public catalog's shape, judged ONCE where it comes off the wire: a payload without its lists or its numbers
 * is unreadable — never "no apps", never a page that quietly prices nothing.
 *
 * @param {*} payload - what `GET /v1/catalog/apps` answered.
 * @returns {PublicCatalog} the same object, now known to be a catalog.
 * @throws {TypeError} on a shape no catalog has.
 */
function readPublicCatalogPayload(payload) {
  const readable =
    payload !== null &&
    typeof payload === 'object' &&
    Array.isArray(payload.apps) &&
    Array.isArray(payload.suggestions) &&
    payload.processing !== null &&
    typeof payload.processing === 'object' &&
    isNonNegativeInteger(payload.processing.rateBps) &&
    isNonNegativeInteger(payload.processing.fixedCents) &&
    isNonNegativeInteger(payload.annualMonthsCharged) &&
    isNonNegativeInteger(payload.trialDays);
  if (!readable) throw new TypeError('The app catalog could not be read.');
  return payload;
}

/**
 * A dimension's published MONTHLY unit price, or null while none is published.
 *
 * @param {{prices?: PublicPrice[]}} dimension
 * @returns {?number}
 */
function monthlyUnitPriceOf(dimension) {
  const row = (dimension.prices || []).find((p) => p.billingCycle === BILLING_CYCLE.MONTHLY);
  return row === undefined ? null : row.unitAmountCents;
}

function dimensionsOfKind(entry, dimensionKind) {
  return [...(entry.dimensions || [])]
    .filter((d) => d.dimensionKind === dimensionKind)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * @param {PublicEntry} entry
 * @returns {PublicDimension[]} the size dimensions in sort order.
 */
function sizeDimensionsOf(entry) {
  return dimensionsOfKind(entry, DIMENSION_KIND.SIZE);
}

/**
 * @param {PublicEntry} entry
 * @returns {PublicDimension[]} the consumables in sort order.
 */
function consumablesOf(entry) {
  return dimensionsOfKind(entry, DIMENSION_KIND.CONSUMABLE);
}

/**
 * Whether an app can be bought today: a per-unit app is priced when every size dimension has a published monthly
 * price — or, for an app priced by nothing but blocks (the Auto-blog, Q-AE-46), when every block price is. A free
 * app and a share-of-recovery app carry no price to publish.
 *
 * @param {PublicEntry} entry
 * @returns {boolean}
 */
function isPriced(entry) {
  if (entry.pricingModel !== PRICING_MODEL.PER_UNIT) return true;
  const sizes = sizeDimensionsOf(entry);
  if (sizes.length > 0) return sizes.every((d) => monthlyUnitPriceOf(d) !== null);
  const consumables = consumablesOf(entry);
  return consumables.length > 0 && consumables.every((c) => monthlyUnitPriceOf(c) !== null);
}

/**
 * The public catalog's row for an app, or `null` when the console does not offer it.
 *
 * @param {PublicCatalog} catalog
 * @param {string} appKey
 * @returns {?PublicEntry}
 */
function publicEntryOf(catalog, appKey) {
  return catalog.apps.find((entry) => entry.appKey === appKey) || null;
}

const bySortOrder = (a, b) => a.sortOrder - b.sortOrder;

/**
 * The anchors a visitor may choose, in catalog order: every anchor the console offers or marks coming soon.
 *
 * @param {PublicCatalog} catalog
 * @returns {PublicEntry[]}
 */
function anchorEntriesOf(catalog) {
  return catalog.apps.filter((entry) => entry.kind === APP_KIND.ANCHOR).sort(bySortOrder);
}

/**
 * The apps a visitor may add beside an anchor: offered, sellable, attached to it, priced by the unit or by a share of
 * recovery — never a free app (those come with the anchor) and never the switchable customer-accounts app (Q-LS-85: a
 * per-company setting, not a line).
 *
 * @param {PublicCatalog} catalog
 * @param {string} anchorAppKey
 * @returns {PublicEntry[]} rows in catalog order
 */
function attachableEntriesOf(catalog, anchorAppKey) {
  return catalog.apps
    .filter(
      (entry) =>
        entry.kind === APP_KIND.APP &&
        entry.isSellable &&
        entry.isOffered &&
        entry.pricingModel !== PRICING_MODEL.INCLUDED_WITH_ANCHOR &&
        // An APP row always carries the anchors it attaches to (the seed writes the list; only an anchor's is null).
        entry.attachesTo.includes(anchorAppKey),
    )
    .sort(bySortOrder);
}

/**
 * The apps that come free with an anchor (Q-AE-4: PalmPuffin and PrepPuffin, called out as free), never the
 * switchable one.
 *
 * @param {PublicCatalog} catalog
 * @param {string} anchorAppKey
 * @returns {PublicEntry[]}
 */
function freeEntriesOf(catalog, anchorAppKey) {
  return catalog.apps
    .filter(
      (entry) =>
        entry.pricingModel === PRICING_MODEL.INCLUDED_WITH_ANCHOR &&
        !getApp(entry.appKey)?.switchable &&
        entry.attachesTo.includes(anchorAppKey),
    )
    .sort(bySortOrder);
}

/**
 * The price book `quoteBasket` reads, from the public catalog: each app's published MONTHLY unit price per size
 * dimension, and the set of apps the console offers.
 *
 * @param {PublicCatalog} catalog
 * @returns {{units: Object<string, Object<string, number>>, offered: string[]}}
 */
function priceBookFromPublicCatalog(catalog) {
  const units = {};
  for (const entry of catalog.apps) {
    for (const dimension of sizeDimensionsOf(entry)) {
      const cents = monthlyUnitPriceOf(dimension);
      if (cents !== null) {
        if (units[entry.appKey] === undefined) units[entry.appKey] = {};
        units[entry.appKey][dimension.dimensionKey] = cents;
      }
    }
  }
  return {
    units,
    offered: catalog.apps.filter((entry) => entry.isOffered).map((entry) => entry.appKey),
  };
}

/**
 * The size dimensions a basket asks a quantity for: every size dimension of an app but the account itself (one,
 * always). A derived dimension is still ASKED at signup — nothing is provisioned yet to count, and the quote needs the
 * intended size; billing derives the real count later.
 *
 * @param {PublicEntry} entry
 * @returns {PublicDimension[]}
 */
function askedDimensionsOf(entry) {
  return sizeDimensionsOf(entry).filter((d) => d.dimensionKey !== DIMENSION_KEY.ACCOUNT);
}

/**
 * The least a dimension's quantity may be: what the anchor includes per parent unit (three registers a location);
 * else one for a DERIVED dimension (an account has a location, a line, a mailbox) and NONE for a DECLARED one — an
 * add-on the visitor chooses (the AI receptionist, Q-AE-27), never bought by default.
 *
 * @param {PublicDimension} dimension
 * @param {Object<string, number>} quantitiesOfApp - the app's quantities so far
 * @returns {number}
 */
function minimumQuantityOf(dimension, quantitiesOfApp) {
  if (dimension.parentDimensionKey && dimension.includedQuantity > 0) {
    const parent = quantitiesOfApp[dimension.parentDimensionKey] ?? 1;
    return parent * dimension.includedQuantity;
  }
  return dimension.quantitySource === QUANTITY_SOURCE.DECLARED ? 0 : 1;
}

/**
 * Price a basket draft against the public catalog: the quote, or the catalog's refusal by CODE — the shape a surface
 * renders from without a try/catch of its own.
 *
 * @param {PublicCatalog} catalog
 * @param {{anchorAppKey: ?string, appKeys: string[], quantities: Object, billingCycle?: string}} basket
 * @returns {{quote: ?Object, refusal: ?{code: string, details: Object}}}
 * @throws {Error} anything that is not a catalog refusal (a malformed draft is a defect, never a refusal).
 */
function quoteDraft(catalog, basket) {
  if (!basket.anchorAppKey) {
    return { quote: null, refusal: { code: APP_CATALOG_REFUSAL.ANCHOR_REQUIRED, details: {} } };
  }
  try {
    return {
      quote: quoteBasket({
        basket: {
          anchorAppKey: basket.anchorAppKey,
          appKeys: basket.appKeys,
          quantities: basket.quantities,
          billingCycle: basket.billingCycle || BILLING_CYCLE.MONTHLY,
        },
        priceBook: priceBookFromPublicCatalog(catalog),
      }),
      refusal: null,
    };
  } catch (error) {
    if (isAppCatalogRefusal(error)) {
      return { quote: null, refusal: { code: error.code, details: error.details } };
    }
    throw error;
  }
}

module.exports = {
  readPublicCatalogPayload,
  monthlyUnitPriceOf,
  sizeDimensionsOf,
  consumablesOf,
  isPriced,
  publicEntryOf,
  anchorEntriesOf,
  attachableEntriesOf,
  freeEntriesOf,
  priceBookFromPublicCatalog,
  askedDimensionsOf,
  minimumQuantityOf,
  quoteDraft,
};
